import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel,
  MenuItem, Paper, Select, Switch, Table, TableBody, TableCell, TableHead,
  TablePagination, TableRow, TextField, Tooltip, Typography, Link, InputAdornment,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch, getConfigPost } from "../getConfig";

interface PartnerizeBrand {
  id: number;
  campaign_id: string;
  display_name: string;
  logo_url: string | null;
  currency: string | null;
  status: string;
  advertiser_id: string;
  network_name: string | null;
  website_url: string | null;
  campaign_status: string | null;
  rate_numeric: number | null;
  rate_model: string | null;
  rate_display: string | null;
  sprive_haircut_percentage: number | null;
  sprive_haircut_override_percentage: number | null;
  effective_haircut_percentage: number;
  haircut_source: "base" | "override";
  last_rate_sync_at: string | null;
  last_sync_at: string | null;
  campaign_count: number;
  enabled_count: number;
  needs_review: boolean;
}

interface Metadata {
  total_brands: number;
  enabled_count: number;
  non_gbp_count: number;
  unrated_count: number;
  approved_count: number;
  requested_count: number;
  live_count: number;
}

const EMPTY_METADATA: Metadata = {
  total_brands: 0, enabled_count: 0, non_gbp_count: 0, unrated_count: 0,
  approved_count: 0, requested_count: 0, live_count: 0,
};

const CAMPAIGN_STATUSES = ["AVAILABLE", "REQUESTED", "INVITED", "APPROVED", "REJECTED"];

const PartnerizeBrandsDashboard: React.FC = () => {
  const [brands, setBrands] = useState<PartnerizeBrand[]>([]);
  const [metadata, setMetadata] = useState<Metadata>(EMPTY_METADATA);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState<number | null>(null);
  const [editingHaircutId, setEditingHaircutId] = useState<number | null>(null);
  const [haircutDraft, setHaircutDraft] = useState("");

  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [testBrand, setTestBrand] = useState<PartnerizeBrand | null>(null);
  const [testResult, setTestResult] = useState<any | null>(null);
  const [testError, setTestError] = useState("");
  const [testLoading, setTestLoading] = useState(false);
  const [joinBrand, setJoinBrand] = useState<PartnerizeBrand | null>(null);
  const [joinResult, setJoinResult] = useState<any | null>(null);
  const [joinError, setJoinError] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");
  const [campaignStatusFilter, setCampaignStatusFilter] = useState("");
  const [needsReview, setNeedsReview] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params: any = { page: page + 1, pageSize, sortBy: "display_name", sortOrder: "ASC" };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (currencyFilter) params.currency = currencyFilter;
      if (campaignStatusFilter) params.campaign_status = campaignStatusFilter;
      if (needsReview) params.needs_review = "true";

      const response = await axios.get<any>(ENDPOINTS.PARTNERIZE_BRANDS, getConfigGet(params));
      if (response.data?.success) {
        setBrands(response.data.data || []);
        setTotal(response.data.pagination?.total || 0);
        setMetadata(response.data.metadata || EMPTY_METADATA);
      } else {
        setError(response.data?.message || "Failed to load Partnerize brands");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load Partnerize brands");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter, currencyFilter, campaignStatusFilter, needsReview]);

  useEffect(() => {
    const timer = setTimeout(fetchBrands, 300);
    return () => clearTimeout(timer);
  }, [fetchBrands]);

  const startEditHaircut = (brand: PartnerizeBrand) => {
    setEditingHaircutId(brand.id);
    setHaircutDraft(
      brand.sprive_haircut_override_percentage === null ? "" : String(brand.sprive_haircut_override_percentage)
    );
  };

  // Blank clears the override so the brand falls back to its synced base rate.
  const saveHaircut = async (brand: PartnerizeBrand) => {
    const trimmed = haircutDraft.trim();
    const value = trimmed === "" ? null : Number(trimmed);
    const base = brand.sprive_haircut_percentage ?? 0;

    if (value !== null && !Number.isFinite(value)) {
      setError("Override must be a number, or blank to clear it");
      return;
    }
    if (value !== null && base + value < 0) {
      setError(`Override would make the haircut negative (base ${base}%)`);
      return;
    }

    setSavingId(brand.id);
    setError("");
    try {
      const { data } = await axios.patch(
        `${ENDPOINTS.PARTNERIZE_BRANDS}/${brand.id}`,
        { sprive_haircut_override_percentage: value },
        { headers: getConfigPatch() }
      );
      const updated: any = (data as any)?.data || {};
      setBrands((prev) => prev.map((b) => (b.id === brand.id ? {
        ...b,
        sprive_haircut_override_percentage: updated.sprive_haircut_override_percentage ?? null,
        effective_haircut_percentage: updated.effective_haircut_percentage ?? b.effective_haircut_percentage,
        haircut_source: updated.haircut_source ?? b.haircut_source,
      } : b)));
      setEditingHaircutId(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to update haircut");
    } finally {
      setSavingId(null);
    }
  };

  const toggleStatus = async (brand: PartnerizeBrand) => {
    const next = brand.status === "enabled" ? "disabled" : "enabled";
    setSavingId(brand.id);
    setError("");
    try {
      await axios.patch(`${ENDPOINTS.PARTNERIZE_BRANDS}/${brand.id}`, { status: next }, { headers: getConfigPatch() });
      setBrands((prev) => prev.map((b) => (b.id === brand.id ? { ...b, status: next } : b)));
      setMetadata((m) => ({
        ...m,
        enabled_count: m.enabled_count + (next === "enabled" ? 1 : -1),
      }));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to update brand");
    } finally {
      setSavingId(null);
    }
  };

  const openDetails = async (brand: PartnerizeBrand) => {
    setDetail({ id: brand.id, display_name: brand.display_name });
    setDetailLoading(true);
    try {
      const response = await axios.get<any>(
        `${ENDPOINTS.PARTNERIZE_BRANDS}/${brand.id}`,
        getConfigGet({})
      );
      setDetail(response.data?.data || response.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load brand details");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const runLinkTest = async (brand: PartnerizeBrand) => {
    setTestBrand(brand);
    setTestResult(null);
    setTestError("");
    setTestLoading(true);
    try {
      const response = await axios.post<any>(
        ENDPOINTS.PARTNERIZE_BRANDS,
        { brand_id: brand.id, action: "test_link" },
        { headers: getConfigPost() }
      );
      setTestResult(response.data?.data || response.data);
    } catch (err: any) {
      setTestError(err?.response?.data?.message || err?.message || "Failed to create tracking link");
    } finally {
      setTestLoading(false);
    }
  };

  const requestJoin = async (brand: PartnerizeBrand) => {
    setJoinBrand(brand);
    setJoinResult(null);
    setJoinError("");
  };

  const confirmJoin = async () => {
    if (!joinBrand) return;
    setJoinLoading(true);
    setJoinError("");
    try {
      const response = await axios.post<any>(
        ENDPOINTS.PARTNERIZE_BRANDS,
        { brand_id: joinBrand.id, action: "join" },
        { headers: getConfigPost() }
      );
      const result = response.data?.data || response.data;
      setJoinResult(result);
      setBrands((prev) =>
        prev.map((b) =>
          b.id === joinBrand.id ? { ...b, campaign_status: result.campaign_status } : b
        )
      );
    } catch (err: any) {
      setJoinError(err?.response?.data?.message || err?.message || "Failed to request join");
    } finally {
      setJoinLoading(false);
    }
  };

  const detailRow = (label: string, value: any) => (
    <Grid container spacing={1} key={label} style={{ padding: "4px 0" }}>
      <Grid item xs={5}>
        <Typography variant="body2" color="textSecondary">{label}</Typography>
      </Grid>
      <Grid item xs={7}>
        <Typography variant="body2" style={{ wordBreak: "break-all" }}>
          {value === null || value === undefined || value === "" ? "—" : String(value)}
        </Typography>
      </Grid>
    </Grid>
  );

  const stat = (label: string, value: number | string) => (
    <Grid item xs={6} sm={3}>
      <Card>
        <CardContent>
          <Typography variant="body2" color="textSecondary">{label}</Typography>
          <Typography variant="h5">{value}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>Partnerize Brands</Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Brands sync in disabled. Enable the one campaign that should represent each advertiser.
      </Typography>

      <Grid container spacing={2} style={{ marginTop: 8, marginBottom: 8 }}>
        {stat("Total brands", metadata.total_brands)}
        {stat("Joined (APPROVED)", metadata.approved_count)}
        {stat("Awaiting approval", metadata.requested_count)}
        {stat("Live in app", metadata.live_count)}
      </Grid>

      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth label="Search brand" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as string); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="enabled">Enabled</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Partnerize status</InputLabel>
              <Select
                value={campaignStatusFilter}
                onChange={(e) => { setCampaignStatusFilter(e.target.value as string); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {CAMPAIGN_STATUSES.map((cs) => (
                  <MenuItem key={cs} value={cs}>{cs}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select value={currencyFilter} onChange={(e) => { setCurrencyFilter(e.target.value as string); setPage(0); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GBP">GBP</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Tooltip title="Advertisers with more than one campaign — one must be picked">
              <Box display="flex" alignItems="center">
                <Switch checked={needsReview} onChange={(e) => { setNeedsReview(e.target.checked); setPage(0); }} />
                <Typography variant="body2">Needs review only</Typography>
              </Box>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {error && <Typography color="error" gutterBottom>{error}</Typography>}

      <Paper>
        {loading && <Box p={2} textAlign="center"><CircularProgress size={28} /></Box>}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Brand</TableCell>
              <TableCell>Campaign</TableCell>
              <TableCell>Advertiser</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Rate</TableCell>
              <TableCell>Sprive haircut</TableCell>
              <TableCell>Partnerize status</TableCell>
              <TableCell align="center">Live in app</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && brands.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center">No brands found</TableCell></TableRow>
            )}
            {brands.map((b) => (
              <TableRow key={b.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    {b.logo_url && (
                      <img src={b.logo_url} alt="" width={28} height={28}
                           style={{ marginRight: 8, objectFit: "contain" }} />
                    )}
                    <Box>
                      {b.website_url
                        ? <Link href={b.website_url} target="_blank" rel="noopener">{b.display_name}</Link>
                        : <span>{b.display_name}</span>}
                      {b.needs_review && (
                        <Tooltip title={`This advertiser has ${b.campaign_count} campaigns, ${b.enabled_count} enabled`}>
                          <Chip label="needs review" size="small" color="secondary" style={{ marginLeft: 8 }} />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{b.campaign_id}</TableCell>
                <TableCell>{b.network_name || b.advertiser_id}</TableCell>
                <TableCell>
                  <Chip label={b.currency || "-"} size="small"
                        color={b.currency === "GBP" ? "primary" : "default"} />
                </TableCell>
                <TableCell>
                  {b.rate_display || <Typography variant="body2" color="textSecondary">—</Typography>}
                </TableCell>
                <TableCell>
                  {editingHaircutId === b.id ? (
                    <Box display="flex" alignItems="center">
                      <TextField
                        size="small"
                        autoFocus
                        value={haircutDraft}
                        placeholder="0"
                        helperText={`base ${b.sprive_haircut_percentage ?? 0}%`}
                        onChange={(e) => setHaircutDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveHaircut(b);
                          if (e.key === "Escape") setEditingHaircutId(null);
                        }}
                        style={{ width: 80 }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                      {savingId === b.id
                        ? <CircularProgress size={18} style={{ marginLeft: 8 }} />
                        : (
                          <>
                            <IconButton size="small" onClick={() => saveHaircut(b)}><CheckIcon fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => setEditingHaircutId(null)}><CloseIcon fontSize="small" /></IconButton>
                          </>
                        )}
                    </Box>
                  ) : (
                    <Tooltip title={b.haircut_source === "override"
                      ? `Base ${b.sprive_haircut_percentage ?? 0}% with a ${b.sprive_haircut_override_percentage}pt override — clear it to revert`
                      : "The rate stamped at sync — click to add a per-brand override"}>
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{ cursor: "pointer" }}
                        onClick={() => startEditHaircut(b)}
                      >
                        <span>{b.effective_haircut_percentage}%</span>
                        {b.haircut_source === "override" && (
                          <Chip label="override" size="small" color="secondary" variant="outlined" style={{ marginLeft: 6 }} />
                        )}
                        <EditIcon fontSize="small" color="disabled" style={{ marginLeft: 4 }} />
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={b.campaign_status || "-"}
                    size="small"
                    color={b.campaign_status === "APPROVED" ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell align="center">
                  {savingId === b.id
                    ? <CircularProgress size={18} />
                    : <Switch checked={b.status === "enabled"} onChange={() => toggleStatus(b)} color="primary" />}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => openDetails(b)}>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                  {b.campaign_status !== "APPROVED" && (
                    <Tooltip title="Request to join campaign">
                      <IconButton size="small" color="secondary" onClick={() => requestJoin(b)}>
                        <GroupAddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={
                      b.campaign_status === "APPROVED"
                        ? "Test redirect link"
                        : "Campaign must be joined first"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => runLinkTest(b)}
                        disabled={b.campaign_status !== "APPROVED"}
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onChangePage={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onChangeRowsPerPage={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{detail?.display_name || "Brand details"}</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box p={2} textAlign="center"><CircularProgress size={28} /></Box>
          ) : (
            <>
              {detailRow("Brand id", detail?.id)}
              {detailRow("Campaign id", detail?.external_key)}
              {detailRow("Advertiser id", detail?.advertiser_id)}
              {detailRow("Network", detail?.network_name)}
              {detailRow("Status", detail?.status)}
              {detailRow("Partnerize status", detail?.campaign_status)}
              {detailRow("Currency", detail?.currency)}
              {detailRow("Rate", detail?.discount_rate)}
              {detailRow("Rate numeric", detail?.rate_numeric)}
              {detailRow("Rate model", detail?.rate_model)}
              {detailRow("Categories", detail?.categories && JSON.stringify(detail.categories))}
              {detailRow("Website", detail?.website_url)}
              {detailRow("Logo", detail?.logo_url)}
              {detailRow("Added", detail?.added_date)}
              {detailRow("Last rate sync", detail?.last_rate_sync_at)}
              {detailRow("Last sync", detail?.last_sync_at)}
              {detailRow("Description", detail?.description)}
              {detail?.rate_breakdown && (
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">Rate breakdown</Typography>
                  <pre style={{ fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                    {JSON.stringify(detail.rate_breakdown, null, 2)}
                  </pre>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!joinBrand} onClose={() => setJoinBrand(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Request to join — {joinBrand?.display_name}</DialogTitle>
        <DialogContent dividers>
          {joinLoading && <Box p={2} textAlign="center"><CircularProgress size={28} /></Box>}
          {!joinLoading && !joinResult && !joinError && (
            <>
              <Typography variant="body2" gutterBottom>
                This sends a join request to the advertiser on Partnerize. They approve it
                manually — the campaign becomes usable once its status reaches APPROVED.
              </Typography>
              {detailRow("Campaign id", joinBrand?.campaign_id)}
              {detailRow("Advertiser id", joinBrand?.advertiser_id)}
              {detailRow("Current status", joinBrand?.campaign_status)}
            </>
          )}
          {!joinLoading && joinError && <Typography color="error">{joinError}</Typography>}
          {!joinLoading && joinResult && (
            <Typography color={joinResult.already_requested ? "textSecondary" : "textPrimary"}>
              {joinResult.message}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {!joinResult && (
            <Button onClick={confirmJoin} color="primary" disabled={joinLoading}>
              Send request
            </Button>
          )}
          <Button onClick={() => setJoinBrand(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!testBrand} onClose={() => setTestBrand(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Test redirect — {testBrand?.display_name}</DialogTitle>
        <DialogContent dividers>
          {testLoading && <Box p={2} textAlign="center"><CircularProgress size={28} /></Box>}
          {!testLoading && testError && <Typography color="error">{testError}</Typography>}
          {!testLoading && testResult && (
            <>
              {detailRow("Campaign id", testResult.campaign_id)}
              {detailRow("Destination", testResult.destination_url)}
              {detailRow("pubref", testResult.pubref)}
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">Tracking url</Typography>
                <Link href={testResult.tracking_url} target="_blank" rel="noopener"
                      style={{ wordBreak: "break-all" }}>
                  {testResult.tracking_url}
                </Link>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {testBrand && (
            <Button onClick={() => runLinkTest(testBrand)} disabled={testLoading}>Retry</Button>
          )}
          <Button onClick={() => setTestBrand(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerizeBrandsDashboard;
